import { useCallback, useEffect, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SELLER_ORDERS_QUERY } from 'api/graphql/queries/order/getOrdersList';
import { PaginationState } from '@tanstack/react-table';
import { Order as OrderType } from 'data/e-commerce/orders';
import { 
  getOrderStatusConfig, 
  getPaymentStatusConfig, 
  getPaymentMethodConfig,
  formatStatusLabel
} from 'utils/orderStatusHelpers';
import { BadgeBg } from 'components/base/Badge';

// Format date as "MMM d, yyyy, h:mm a" - e.g., "Dec 12, 2025, 12:56 PM"
const formatOrderDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Get month abbreviation
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    
    // Get day and year
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Get time in 12-hour format with AM/PM
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Return original string if formatting fails
  }
};

const transformSellerOrders = (sellerOrders: any[]): OrderType[] => {
  return sellerOrders.map((item) => {
    // Get status configurations
    const orderStatus = getOrderStatusConfig(item.order_status?.code);
    const paymentStatus = getPaymentStatusConfig(item.payment_status?.code);
    const paymentMethodConfig = getPaymentMethodConfig(item.payment_method?.code);
    
    // Create state badge configuration, using the same styling pattern as other statuses
    const stateConfig = getOrderStatusConfig(item.state);
    
    // Transform the API response to match the required Order format
    return {
      // Convert orderId to number to match the Order interface
      orderId: parseInt(item.order_id, 10) || 0,
      // Add order_number to the transformed data
      order_number: item.order_number || `ORD-${item.order_id}`,
      total: item.total_amount || 0,
      total_seller_amount: item.total_seller_amount || 0,
      total_commission: item.total_commission || 0,
      customer: item.customer_name || 'Unknown Customer',
      state: {
        label: stateConfig.label || formatStatusLabel(item.state) || 'Unknown',
        type: stateConfig.type as BadgeBg,
        icon: stateConfig.icon
      },
      payment_status: {
        // Use either the custom label from our configuration or the one from API
        label: paymentStatus.label || item.payment_status?.label || 'Unknown',
        type: paymentStatus.type as BadgeBg,
        icon: paymentStatus.icon
      },
      fulfilment_status: {
        // Use either the custom label from our configuration or the one from API
        label: orderStatus.label || item.order_status?.label || 'Unknown',
        type: orderStatus.type as BadgeBg,
        icon: orderStatus.icon
      },
      payment_method: {
        label: paymentMethodConfig.label || item.payment_method?.label || 'Unknown',
        code: item.payment_method?.code || '',
        type: paymentMethodConfig.type as BadgeBg,
        icon: paymentMethodConfig.icon
      },
      delivery_type: item.shipping_method?.label || 'Standard Delivery',
      date: formatOrderDate(item.date_order),
      has_transaction_id: item.has_transaction_id || false,
      shipping_type: item.shipping_type || 'other' // Add shipping_type from API response
    };
  });
};

interface OrdersDataOptions {
  initialPageIndex?: number;
  initialPageSize?: number;
}

interface FilterOptions {
  customerName?: string;
  orderNumber?: string;
  orderStatus?: string | string[] | "all";
  state?: string | string[] | "all"; // Added state filter
  paymentStatus?: string | "all";
  shippingMethod?: string | "all";
  fromDate?: string;
  toDate?: string; 
}

interface SortOptions {
  orderNumber?: number;
  orderDate?: number;
  totalAmount?: number;
}

export interface OrdersDataResult {
  tableData: OrderType[];
  loading: boolean;
  error: any;
  paginationCount: {
    totalPages: number;
    totalCount: number;
  };
  pagination: PaginationState;
  handlePageChange: (newPageIndex: number) => void;
  handlePageSizeChange: (newPagination: PaginationState) => void;
  fetchOrders: () => void;
  paymentStatusCounts: { code: string, count: number }[]; // Kept for backward compatibility
  orderStateCounts: { code: string, label: string, count: number }[]; // Added new property for order state counts
  calculateSumAll: () => number;
}

export function useOrdersData(
  filterOptions: FilterOptions = {},
  sortOptions: SortOptions = {},
  options: OrdersDataOptions = {}
): OrdersDataResult {
  // State for table data
  const [tableData, setTableData] = useState<OrderType[]>([]);
  
  // State for pagination
  const [paginationCount, setPaginationCount] = useState({
    totalPages: 0,
    totalCount: 0
  });
  
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: options.initialPageIndex || 0,
    pageSize: options.initialPageSize || 10,
  });
  
  // Function to build active filters for API calls
  const getActiveFilters = useCallback(() => {
    const filters: any = {};
    
    if (filterOptions.customerName) filters.customer_name = filterOptions.customerName;
    if (filterOptions.orderNumber) filters.order_number = filterOptions.orderNumber;
    if (filterOptions.orderStatus && filterOptions.orderStatus !== "all") {
      // Handle both string and array input for order_status
      if (Array.isArray(filterOptions.orderStatus)) {
        filters.order_status = filterOptions.orderStatus;
      } else {
        filters.order_status = [filterOptions.orderStatus];
      }
    }
    if (filterOptions.state && filterOptions.state !== "all") {
      // Handle both string and array input for state
      if (Array.isArray(filterOptions.state)) {
        filters.state = filterOptions.state;
      } else {
        filters.state = [filterOptions.state];
      }
    }
    if (filterOptions.paymentStatus && filterOptions.paymentStatus !== "all") filters.payment_status = filterOptions.paymentStatus;
    if (filterOptions.shippingMethod && filterOptions.shippingMethod !== "all") filters.shipping_method = filterOptions.shippingMethod;
    
    if (filterOptions.fromDate) {
      filters.from_date = filterOptions.fromDate;
    }
    
    if (filterOptions.toDate) {
      filters.to_date = filterOptions.toDate;
    }
    
    return filters;
  }, [filterOptions]);
  
  // Function to build sort options for API calls
  const getSortOptions = useCallback(() => {
    const activeSortOptions: any = {};
    
    if (sortOptions.orderNumber !== undefined) {
      activeSortOptions.order_number = sortOptions.orderNumber;
    }
    
    if (sortOptions.orderDate !== undefined) {
      activeSortOptions.order_date = sortOptions.orderDate;
    }
    
    if (sortOptions.totalAmount !== undefined) {
      // Use total_amount to match the GraphQL API field
      activeSortOptions.total_amount = sortOptions.totalAmount;
    }
    
    return Object.keys(activeSortOptions).length > 0 ? activeSortOptions : undefined;
  }, [sortOptions]);
  
  // Query for fetching orders
  const [fetchOrdersQuery, { loading, error, data }] = useLazyQuery(SELLER_ORDERS_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  
  // Stable references to current filter and sort values to avoid infinite loops
  const currentFilters = useCallback(getActiveFilters, [getActiveFilters]);
  const currentSortOptions = useCallback(getSortOptions, [getSortOptions]);
  
  // Function to fetch orders with current pagination, filter, and sort options
  const fetchOrders = useCallback(() => {
    fetchOrdersQuery({
      variables: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        filter: currentFilters(),
        sort: currentSortOptions(),
      },
    });
  }, [fetchOrdersQuery, pagination.pageIndex, pagination.pageSize, currentFilters, currentSortOptions]);
  
  // Initial fetch on component mount using a ref to ensure it runs only once
  const initialFetchRef = useRef(false);
  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchOrders();
      initialFetchRef.current = true;
    }
  }, [fetchOrders]);
  
  // Pagination handlers
  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev: PaginationState) => {
      const maxPageIndex = Math.max(0, paginationCount.totalPages - 1);
      return {
        ...prev,
        pageIndex: Math.min(newPageIndex, maxPageIndex),
      };
    });
    
    fetchOrdersQuery({
      variables: {
        page: Math.min(newPageIndex + 1, paginationCount.totalPages),
        pageSize: pagination.pageSize,
        filter: getActiveFilters(),
        sort: getSortOptions(),
      },
    });
  };
  
  // Handle page size changes
  const handlePageSizeChange = (newPagination: PaginationState) => {
    setPagination(newPagination);
    
    // Reset to first page when changing page size
    fetchOrdersQuery({
      variables: {
        page: 1,
        pageSize: newPagination.pageSize,
        filter: getActiveFilters(),
        sort: getSortOptions(),
      },
    });
  };
  
  // Update the table data when API response is received
  useEffect(() => {
    if (data?.sellerOrders?.orders && data.sellerOrders.orders.length > 0) {
      const transformed = transformSellerOrders(data.sellerOrders.orders);
      setTableData(transformed);
      
      // Update pagination counts
      setPaginationCount({
        totalCount: data.sellerOrders.pagination.total_count || 0,
        totalPages: Math.max(1, data.sellerOrders.pagination.total_pages || 1),
      });
    } else {
      setTableData([]);
    }
  }, [data]);
  
  // Use refs to track previous filter and sort values to avoid unnecessary rerenders
  const prevFilterOptionsRef = useRef('');
  const prevSortOptionsRef = useRef('');
  
  // Refetch data when filter or sort options change
  useEffect(() => {
    const currentFilterOptionsString = JSON.stringify(filterOptions);
    const currentSortOptionsString = JSON.stringify(sortOptions);
    
    // Only update if the values have actually changed
    if (
      currentFilterOptionsString !== prevFilterOptionsRef.current ||
      currentSortOptionsString !== prevSortOptionsRef.current
    ) {
      setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }));
      
      fetchOrdersQuery({
        variables: {
          page: 1,
          pageSize: pagination.pageSize,
          filter: currentFilters(),
          sort: currentSortOptions(),
        },
      });
      
      // Update refs with current values
      prevFilterOptionsRef.current = currentFilterOptionsString;
      prevSortOptionsRef.current = currentSortOptionsString;
    }
  }, [filterOptions, sortOptions, pagination.pageSize, fetchOrdersQuery, currentFilters, currentSortOptions]);
  
  // Calculate the total count of all order states
  const calculateSumAll = useCallback(() => {
    if (!data?.sellerOrders?.order_state_counts) {
      return 0;
    }
    
    // First check if a "total" entry exists in the state counts
    const totalEntry = data.sellerOrders.order_state_counts.find(
      (item: { code: string; count: number }) => item.code === 'total'
    );
    
    // If total entry exists, use it directly
    if (totalEntry) {
      return totalEntry.count;
    }
    
    // Otherwise, sum up all state counts except 'canceled' and 'pending'
    let sum = 0;
    data.sellerOrders.order_state_counts.forEach((item: { code: string; count: number }) => {
      // Skip canceled and pending counts
      if (item.code !== 'canceled' && item.code !== 'pending') {
        sum += item.count;
      }
    });
    
    return sum;
  }, [data?.sellerOrders?.order_state_counts]);

  return {
    tableData,
    loading,
    error,
    paginationCount,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    fetchOrders,
    paymentStatusCounts: [], // Keep for backwards compatibility
    orderStateCounts: data?.sellerOrders?.order_state_counts || [],
    calculateSumAll,
  };
}
